import { useState, useRef, useCallback, useEffect } from 'react';

interface UsePollyTTSOptions {
  idToken: string | null;
  language?: string;
}

interface UsePollyTTSReturn {
  ttsEnabled: boolean;
  toggleTTS: () => void;
  isSpeaking: boolean;
  speakFullText: (text: string) => void;
  stop: () => void;
  playbackSpeed: number;
  setPlaybackSpeed: (speed: number) => void;
}

const STORAGE_KEY = 'paila-tts-enabled';
const SPEED_STORAGE_KEY = 'paila-tts-speed';
const REGION = 'eu-west-2';

/**
 * Target characters per chunk sent to Polly.
 * Larger chunks = more natural prosody across sentences, fewer audio gaps.
 */
const CHUNK_MIN_LENGTH = 200;

/** Acronyms that Polly should spell out letter-by-letter. */
const SPELL_OUT_ACRONYMS = ['UEL', 'SITS', 'CELCAT', 'BPMN', 'CSS', 'HTML', 'PDF', 'AWS', 'API'];

/** Words with custom pronunciation (IPA phonemes). */
const PHONEME_MAP: Record<string, string> = {
  'PAILA': 'peɪlə',
};

/** Voice configuration per language. */
const VOICE_CONFIG: Record<string, { VoiceId: string; Engine: 'neural' | 'standard'; LanguageCode: string }> = {
  en: { VoiceId: 'Amy', Engine: 'neural', LanguageCode: 'en-GB' },
  hi: { VoiceId: 'Kajal', Engine: 'neural', LanguageCode: 'hi-IN' },
};

/** Strip markdown formatting that Polly would read literally. */
function stripMarkdown(text: string): string {
  return text
    .replace(/^#{1,6}\s+/gm, '')         // headers
    .replace(/\*\*(.+?)\*\*/g, '$1')     // bold
    .replace(/\*(.+?)\*/g, '$1')         // italic
    .replace(/`(.+?)`/g, '$1')           // inline code
    .replace(/^\s*[-*•]\s+/gm, '')       // bullet points
    .replace(/^\s*\d+\.\s+/gm, '')       // numbered lists
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')  // markdown links — keep text
    .replace(/---+/g, '');               // horizontal rules
}

/** Convert plain text to SSML, spelling out acronyms and escaping XML. */
function toSSML(text: string): string {
  let cleaned = stripMarkdown(text);

  // Escape XML special characters
  cleaned = cleaned
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Replace words with custom pronunciation (phonemes)
  for (const [word, ipa] of Object.entries(PHONEME_MAP)) {
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    cleaned = cleaned.replace(regex, `<phoneme alphabet="ipa" ph="${ipa}">${word}</phoneme>`);
  }

  // Replace acronyms with <say-as> tags (case-insensitive match on word boundaries)
  for (const acronym of SPELL_OUT_ACRONYMS) {
    const regex = new RegExp(`\\b${acronym}\\b`, 'gi');
    cleaned = cleaned.replace(regex, `<say-as interpret-as="characters">${acronym}</say-as>`);
  }

  return `<speak>${cleaned}</speak>`;
}

/** Convert plain text to SSML for non-English languages (no acronym/phoneme processing). */
function toPlainSSML(text: string): string {
  let cleaned = stripMarkdown(text);
  cleaned = cleaned.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return `<speak>${cleaned}</speak>`;
}

/**
 * Find the last sentence boundary (. ? !) in text.
 * Returns the index after the punctuation, or -1 if none found.
 */
function lastSentenceBoundary(text: string): number {
  for (let i = text.length - 1; i >= 0; i--) {
    if (text[i] === '.' || text[i] === '?' || text[i] === '!' || text[i] === '।') {
      return i + 1;
    }
  }
  return -1;
}

/**
 * Split text into chunks for Polly synthesis.
 * Each chunk targets CHUNK_MIN_LENGTH characters, split at sentence boundaries.
 * This produces fewer, larger audio files with natural cross-sentence prosody.
 */
function splitIntoChunks(text: string): string[] {
  const chunks: string[] = [];
  let remaining = text;

  while (remaining.length > CHUNK_MIN_LENGTH) {
    // Look for a sentence boundary at or after the minimum length
    const searchFrom = remaining.slice(0, CHUNK_MIN_LENGTH * 2);
    let splitAt = -1;

    // Find the first sentence boundary at or after CHUNK_MIN_LENGTH
    for (let i = CHUNK_MIN_LENGTH; i < searchFrom.length; i++) {
      if (remaining[i - 1] === '.' || remaining[i - 1] === '?' || remaining[i - 1] === '!' || remaining[i - 1] === '।') {
        splitAt = i;
        break;
      }
    }

    // If no boundary found after the minimum, look before it
    if (splitAt === -1) {
      const boundary = lastSentenceBoundary(remaining.slice(0, CHUNK_MIN_LENGTH));
      if (boundary > 0) {
        splitAt = boundary;
      }
    }

    // If still no boundary, take the whole minimum length
    if (splitAt === -1) {
      splitAt = CHUNK_MIN_LENGTH;
    }

    chunks.push(remaining.slice(0, splitAt).trim());
    remaining = remaining.slice(splitAt).trim();
  }

  if (remaining.trim()) {
    chunks.push(remaining.trim());
  }

  return chunks;
}

export default function usePollyTTS({ idToken, language = 'en' }: UsePollyTTSOptions): UsePollyTTSReturn {
  const [ttsEnabled, setTtsEnabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [playbackSpeed, setPlaybackSpeedState] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(SPEED_STORAGE_KEY);
      return stored ? parseFloat(stored) : 1;
    } catch {
      return 1;
    }
  });

  // Refs for mutable state that doesn't trigger re-renders
  const clientRef = useRef<any>(null);
  const clientPromiseRef = useRef<Promise<any> | null>(null);
  const audioQueueRef = useRef<Promise<ArrayBuffer>[]>([]);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const blobUrlsRef = useRef<string[]>([]);
  const isPlayingRef = useRef(false);
  const stoppedRef = useRef(false);
  const ttsEnabledRef = useRef(ttsEnabled);
  const playbackSpeedRef = useRef(playbackSpeed);

  // Keep refs in sync with state
  useEffect(() => {
    ttsEnabledRef.current = ttsEnabled;
  }, [ttsEnabled]);

  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;
    // Update currently playing audio in real-time
    if (currentAudioRef.current) {
      currentAudioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  const setPlaybackSpeed = useCallback((speed: number) => {
    setPlaybackSpeedState(speed);
    try {
      localStorage.setItem(SPEED_STORAGE_KEY, String(speed));
    } catch {
      // ignore
    }
  }, []);

  const toggleTTS = useCallback(() => {
    setTtsEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  /** Lazily initialise the Polly client using Cognito Identity Pool credentials. */
  const getClient = useCallback(async () => {
    if (clientRef.current) return clientRef.current;
    if (clientPromiseRef.current) return clientPromiseRef.current;

    const config = window.__PAILA_CONFIG__;
    const identityPoolId = config?.identityPoolId;
    const userPoolId = config?.userPoolId;

    if (!identityPoolId || !userPoolId || !idToken) return null;

    clientPromiseRef.current = (async () => {
      const [{ PollyClient }, { fromCognitoIdentityPool }] = await Promise.all([
        import('@aws-sdk/client-polly'),
        import('@aws-sdk/credential-providers'),
      ]);

      const userPoolProviderUrl = `cognito-idp.${REGION}.amazonaws.com/${userPoolId}`;
      const client = new PollyClient({
        region: REGION,
        credentials: fromCognitoIdentityPool({
          identityPoolId,
          logins: { [userPoolProviderUrl]: idToken },
          clientConfig: { region: REGION },
        }),
      });

      clientRef.current = client;
      clientPromiseRef.current = null;
      return client;
    })();

    return clientPromiseRef.current;
  }, [idToken]);

  /** Synthesize a text chunk and return the audio bytes. */
  const synthesize = useCallback(
    async (text: string): Promise<ArrayBuffer> => {
      const client = await getClient();
      if (!client) throw new Error('Polly client not available');

      const voice = VOICE_CONFIG[language] || VOICE_CONFIG['en'];
      const ssml = language === 'en' ? toSSML(text) : toPlainSSML(text);

      const { SynthesizeSpeechCommand } = await import('@aws-sdk/client-polly');
      const command = new SynthesizeSpeechCommand({
        OutputFormat: 'mp3',
        Text: ssml,
        TextType: 'ssml',
        VoiceId: voice.VoiceId as 'Amy' | 'Kajal',
        Engine: voice.Engine,
        LanguageCode: voice.LanguageCode as 'en-GB' | 'hi-IN',
      });

      const response = await client.send(command);
      // AudioStream is a ReadableStream or Blob depending on environment
      const stream = response.AudioStream;
      if (stream instanceof Blob) {
        return stream.arrayBuffer();
      }
      // For ReadableStream (browser)
      const reader = stream.getReader();
      const chunks: Uint8Array[] = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
      }
      const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
      const result = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of chunks) {
        result.set(chunk, offset);
        offset += chunk.length;
      }
      return result.buffer;
    },
    [getClient, language]
  );

  /** Play queued audio blobs sequentially. */
  const processQueue = useCallback(async () => {
    if (isPlayingRef.current) return;
    isPlayingRef.current = true;
    setIsSpeaking(true);

    while (audioQueueRef.current.length > 0 && !stoppedRef.current) {
      const promise = audioQueueRef.current.shift()!;
      try {
        const buffer = await promise;
        if (stoppedRef.current) break;

        const blob = new Blob([buffer], { type: 'audio/mpeg' });
        const url = URL.createObjectURL(blob);
        blobUrlsRef.current.push(url);

        await new Promise<void>((resolve, reject) => {
          const audio = new Audio(url);
          audio.playbackRate = playbackSpeedRef.current;
          currentAudioRef.current = audio;
          audio.onended = () => resolve();
          audio.onerror = () => reject(new Error('Audio playback error'));
          audio.play().catch(reject);
        });
      } catch (err) {
        if (!stoppedRef.current) {
          console.error('TTS playback error:', err);
        }
      }
    }

    currentAudioRef.current = null;
    isPlayingRef.current = false;
    setIsSpeaking(false);
  }, []);

  /** Enqueue a chunk of text for synthesis and playback. */
  const enqueueChunk = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const promise = synthesize(trimmed);
      audioQueueRef.current.push(promise);
      processQueue();
    },
    [synthesize, processQueue]
  );

  /** Speak complete text — splits into chunks for natural prosody. */
  const speakFullText = useCallback(
    (text: string) => {
      if (!ttsEnabledRef.current) return;

      stoppedRef.current = false;

      const chunks = splitIntoChunks(text);
      for (const chunk of chunks) {
        enqueueChunk(chunk);
      }
    },
    [enqueueChunk]
  );

  /** Stop all playback immediately. */
  const stop = useCallback(() => {
    stoppedRef.current = true;
    audioQueueRef.current = [];

    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.src = '';
      currentAudioRef.current = null;
    }

    // Revoke blob URLs
    for (const url of blobUrlsRef.current) {
      URL.revokeObjectURL(url);
    }
    blobUrlsRef.current = [];

    isPlayingRef.current = false;
    setIsSpeaking(false);
  }, []);

  // Stop playback when language changes so queued audio in the old language doesn't play
  const prevLanguageRef = useRef(language);
  useEffect(() => {
    if (prevLanguageRef.current !== language) {
      stop();
      prevLanguageRef.current = language;
    }
  }, [language, stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stoppedRef.current = true;
      audioQueueRef.current = [];

      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.src = '';
      }

      for (const url of blobUrlsRef.current) {
        URL.revokeObjectURL(url);
      }

      clientRef.current = null;
    };
  }, []);

  return { ttsEnabled, toggleTTS, isSpeaking, speakFullText, stop, playbackSpeed, setPlaybackSpeed };
}
